import { ChangeEvent, useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Textarea from "../../../components/ui/Textarea";
import {
	useUpdateAnnouncementMutation,
	type Announcement,
} from "../../../store/api/announcementsApi";
import { useListLeasesQuery } from "../../../store/api/leasesApi";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";

interface EditAnnouncementModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	announcement: Announcement | null;
	isLoading?: boolean;
}

const PRIORITY_OPTIONS = [
	{ value: "info", label: "Info" },
	{ value: "warning", label: "Warning" },
	{ value: "success", label: "Success" },
	{ value: "urgent", label: "Urgent" },
];

const STATUS_OPTIONS = [
	{ value: "draft", label: "Draft" },
	{ value: "active", label: "Active" },
	{ value: "archived", label: "Archived" },
];

const ROLE_OPTIONS = [
	{ value: "tenant", label: "Tenants" },
	{ value: "admin", label: "Admins" },
	{ value: "agent", label: "Agents" },
	{ value: "technician", label: "Technicians" },
];

const EditAnnouncementModal: React.FC<EditAnnouncementModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	announcement,
	isLoading: externalLoading,
}) => {
	const [formData, setFormData] = useState<{
		title: string;
		content: string;
		priority: "success" | "warning" | "urgent" | "info";
		status: string;
		target_roles: string[];
		target_property_ids: string[];
		target_lease_ids: string[];
		publish_date: string;
		expiry_date: string;
		action_link: string;
		action_text: string;
		send_email: boolean;
	}>({
		title: "",
		content: "",
		priority: "info",
		status: "draft",
		target_roles: [],
		target_property_ids: [],
		target_lease_ids: [],
		publish_date: "",
		expiry_date: "",
		action_link: "",
		action_text: "",
		send_email: false,
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	const [updateAnnouncement, { isLoading: isUpdating }] =
		useUpdateAnnouncementMutation();

	const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });
	const { data: leasesData } = useListLeasesQuery({ page_size: 1000 });

	const propertyOptions =
		propertiesData?.results?.map((p) => ({
			value: p.id,
			label: `${p.name} - ${p.address}`,
		})) || [];

	const leaseOptions =
		leasesData?.results?.map((l) => ({
			value: l.id,
			label: `${l.lease_code || l.id} - ${l.property_name || "N/A"}`,
		})) || [];

	const isLoading = externalLoading || isUpdating;

	useEffect(() => {
		if (announcement) {
			// Format dates for datetime-local input
			const formatDateForInput = (dateString?: string) => {
				if (!dateString) return "";
				const date = new Date(dateString);
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");
				const hours = String(date.getHours()).padStart(2, "0");
				const minutes = String(date.getMinutes()).padStart(2, "0");
				return `${year}-${month}-${day}T${hours}:${minutes}`;
			};

			setFormData({
				title: announcement.title || "",
				content: announcement.content || "",
				priority: announcement.priority || "info",
				status: announcement.status || "draft",
				target_roles: announcement.target_roles || [],
				target_property_ids: announcement.target_property_ids || [],
				target_lease_ids: announcement.target_lease_ids || [],
				publish_date: formatDateForInput(announcement.publish_date),
				expiry_date: formatDateForInput(announcement.expiry_date),
				action_link: announcement.action_link || "",
				action_text: announcement.action_text || "",
				send_email: false,
			});
		}
	}, [announcement]);

	const handleInputChange = (
		field: string,
		value: string | string[] | boolean,
	) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const handleRoleToggle = (role: string) => {
		setFormData((prev) => ({
			...prev,
			target_roles: prev.target_roles.includes(role)
				? prev.target_roles.filter((r) => r !== role)
				: [...prev.target_roles, role],
		}));
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.title.trim()) {
			newErrors.title = "Title is required";
		}
		if (!formData.content.trim()) {
			newErrors.content = "Content is required";
		}
		if (formData.publish_date && formData.expiry_date) {
			if (new Date(formData.publish_date) >= new Date(formData.expiry_date)) {
				newErrors.expiry_date = "Expiry date must be after publish date";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!announcement || !validate()) return;

		try {
			await updateAnnouncement({
				id: announcement.id,
				data: {
					title: formData.title,
					content: formData.content,
					priority: formData.priority,
					status: formData.status as "draft" | "active" | "archived",
					target_roles: formData.target_roles.length > 0 ? formData.target_roles : undefined,
					target_property_ids: formData.target_property_ids.length > 0 ? formData.target_property_ids : undefined,
					target_lease_ids: formData.target_lease_ids.length > 0 ? formData.target_lease_ids : undefined,
					publish_date: formData.publish_date || undefined,
					expiry_date: formData.expiry_date || undefined,
					action_link: formData.action_link || undefined,
					action_text: formData.action_text || undefined,
					send_email: formData.send_email,
				},
			}).unwrap();

			onSuccess();
		} catch (error: any) {
			console.error("Failed to update announcement:", error);
			alert(
				error?.data?.detail || "Failed to update announcement. Please try again.",
			);
		}
	};

	if (!isOpen || !announcement) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border sticky top-0 bg-card z-10">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Edit Announcement
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Update announcement details
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
						/>
					</div>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Title */}
					<Input
						label="Title *"
						value={formData.title}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							handleInputChange("title", e.target.value)
						}
						error={errors.title}
						placeholder="Enter announcement title"
						required
					/>

					{/* Content */}
					<Textarea
						label="Content *"
						value={formData.content}
						onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
							handleInputChange("content", e.target.value)
						}
						error={errors.content}
						placeholder="Enter announcement content"
						rows={6}
						required
					/>

					{/* Priority and Status */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<Select
							label="Priority"
							options={PRIORITY_OPTIONS}
							value={formData.priority}
							onValueChange={(value) =>
								handleInputChange("priority", String(value))
							}
						/>

						<Select
							label="Status"
							options={STATUS_OPTIONS}
							value={formData.status}
							onValueChange={(value) =>
								handleInputChange("status", String(value))
							}
						/>
					</div>

					{/* Targeting */}
					<div className="space-y-4">
						<h3 className="font-medium text-foreground">Targeting (Optional)</h3>

						{/* Target Roles */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Target Roles (leave empty for all roles)
							</label>
							<div className="flex flex-wrap gap-2">
								{ROLE_OPTIONS.map((role) => (
									<button
										key={role.value}
										type="button"
										onClick={() => handleRoleToggle(role.value)}
										className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
											formData.target_roles.includes(role.value)
												? "bg-primary text-primary-foreground"
												: "bg-muted text-muted-foreground hover:bg-muted/80"
										}`}
									>
										{role.label}
									</button>
								))}
							</div>
						</div>

						{/* Target Properties */}
						<Select
							label="Target Properties (leave empty for all properties)"
							options={[
								{ value: "", label: "All Properties" },
								...propertyOptions,
							]}
							value=""
							onValueChange={(value) => {
								if (value && !formData.target_property_ids.includes(String(value))) {
									handleInputChange("target_property_ids", [
										...formData.target_property_ids,
										String(value),
									]);
								}
							}}
						/>
						{formData.target_property_ids.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{formData.target_property_ids.map((propId) => {
									const prop = propertyOptions.find((p) => p.value === propId);
									return (
										<span
											key={propId}
											className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-2"
										>
											{prop?.label || propId}
											<button
												type="button"
												onClick={() =>
													handleInputChange(
														"target_property_ids",
														formData.target_property_ids.filter((id) => id !== propId),
													)
												}
											>
												<Icon name="X" size={14} />
											</button>
										</span>
									);
								})}
							</div>
						)}

						{/* Target Leases */}
						<Select
							label="Target Leases (leave empty for all leases)"
							options={[
								{ value: "", label: "All Leases" },
								...leaseOptions,
							]}
							value=""
							onValueChange={(value) => {
								if (value && !formData.target_lease_ids.includes(String(value))) {
									handleInputChange("target_lease_ids", [
										...formData.target_lease_ids,
										String(value),
									]);
								}
							}}
						/>
						{formData.target_lease_ids.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{formData.target_lease_ids.map((leaseId) => {
									const lease = leaseOptions.find((l) => l.value === leaseId);
									return (
										<span
											key={leaseId}
											className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-2"
										>
											{lease?.label || leaseId}
											<button
												type="button"
												onClick={() =>
													handleInputChange(
														"target_lease_ids",
														formData.target_lease_ids.filter((id) => id !== leaseId),
													)
												}
											>
												<Icon name="X" size={14} />
											</button>
										</span>
									);
								})}
							</div>
						)}
					</div>

					{/* Scheduling */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<Input
							label="Publish Date (optional)"
							type="datetime-local"
							value={formData.publish_date}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								handleInputChange("publish_date", e.target.value)
							}
						/>

						<Input
							label="Expiry Date (optional)"
							type="datetime-local"
							value={formData.expiry_date}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								handleInputChange("expiry_date", e.target.value)
							}
							error={errors.expiry_date}
						/>
					</div>

					{/* Action Link */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<Input
							label="Action Link (optional)"
							value={formData.action_link}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								handleInputChange("action_link", e.target.value)
							}
							placeholder="https://example.com"
						/>

						<Input
							label="Action Text (optional)"
							value={formData.action_text}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								handleInputChange("action_text", e.target.value)
							}
							placeholder="Click here"
						/>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							{isLoading ? "Updating..." : "Update Announcement"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditAnnouncementModal;
